import React, { useMemo, useState } from 'react';
import ProgressBar from 'react-bootstrap/ProgressBar';

import { ApiObject, Company, Program, School } from 'app/types';
import Text from 'common/components/Text/Text';

import styles from 'features/explore/school/SchoolDetail.module.scss';
import { useMediaQuery } from 'react-responsive';
import { AttributeComponent } from './Overview';
import { entityIsProgram, entityIsSchool } from 'common/utils';

interface CompProps {
  entity?: ApiObject<School> | ApiObject<Program>;
  careerRef: React.RefObject<HTMLDivElement>;
}

export const CareerSection = ({ entity, careerRef }: CompProps): React.ReactElement => {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const [activeCategory, setActiveCategory] = useState<string>(
    entityIsSchool(entity?.data)
      ? Object.keys(entity?.data.alumni_information || {})[0]
      : Object.keys(entity?.data.school.alumni_information || {})[0]
  );

  const getMaxFromCategory = (category: string) => {
    const values: number[] = entityIsSchool(entity?.data)
      ? entity?.data?.alumni_information?.[category]
        ? Object.values(entity?.data?.alumni_information[category] || {})
        : []
      : entity?.data.school.alumni_information?.[category]
      ? Object.values(entity?.data.school.alumni_information[category] || {})
      : [];
    return Math.max(...values);
  };
  const schoolHasRanking = useMemo(
    () => entityIsSchool(entity?.data) && entity?.data.school_rankings?.[0],
    [entity]
  );
  const programSchoolHasRanking = useMemo(
    () => entityIsProgram(entity?.data) && entity?.data.school.school_rankings?.[0],
    [entity]
  );
  const entityHasStudentsData = useMemo(
    () => entity?.data.working_after_months && entity?.data.working_after_percentage,
    [entity]
  );
  const programSchoolHasStudentsData = useMemo(
    () =>
      entityIsProgram(entity?.data) &&
      entity?.data.school.working_after_months &&
      entity?.data.school.working_after_percentage,
    [entity]
  );
  const entityRanking = useMemo(
    () =>
      entityIsSchool(entity?.data)
        ? entity?.data.school_rankings[0]
        : entityIsProgram(entity?.data)
        ? entity?.data.school.school_rankings[0]
        : undefined,
    [entity]
  );
  const studentsWorkingAfterMonths = useMemo(
    () =>
      entity?.data.working_after_months
        ? entity.data.working_after_months
        : entityIsProgram(entity?.data) && entity?.data.school.working_after_months
        ? entity.data.school.working_after_months
        : undefined,
    [entity]
  );
  const studentsWorkingPercentage = useMemo(
    () =>
      entity?.data.working_after_percentage
        ? entity.data.working_after_percentage
        : entityIsProgram(entity?.data) && entity?.data.school.working_after_percentage
        ? entity.data.school.working_after_percentage
        : undefined,
    [entity]
  );
  return (
    <div className={styles.sectionContainer} id="career" ref={careerRef}>
      <div className={styles.sectionTitle}>
        <div className={styles.blueBlock} />
        <h2 className={styles.titleText}>Career</h2>
      </div>
      {!!(
        schoolHasRanking ||
        programSchoolHasRanking ||
        entityHasStudentsData ||
        programSchoolHasStudentsData
      ) && (
        <>
          <h3 className={styles.subtitle} style={!isMobile ? { marginTop: '8px' } : {}}>
            Employability
          </h3>
          {/* <div className={`${styles.sectionAttributes} row row-sm-6 row-lg-4`}></div> */}
          <div className="d-flex flex-row mt-4 gap-4">
            {schoolHasRanking && (
              <AttributeComponent
                attribute={`${entityRanking?.ranking.name}\n (${entityRanking?.ranking.year})`}
                value={`#${entityRanking?.score}`}
              />
            )}
            {programSchoolHasRanking && (
              <AttributeComponent
                attribute={`${entityRanking?.ranking.name}\n (${entityRanking?.ranking.year})`}
                value={`#${entityRanking?.score}`}
              />
            )}
            {entityHasStudentsData ? (
              <AttributeComponent
                attribute={`Students working after ${studentsWorkingAfterMonths} months`}
                value={`${studentsWorkingPercentage}%`}
              />
            ) : (
              programSchoolHasStudentsData && (
                <AttributeComponent
                  attribute={`Students working after ${studentsWorkingAfterMonths} months`}
                  value={`${studentsWorkingPercentage}%`}
                />
              )
            )}
          </div>
        </>
      )}
      {entityIsSchool(entity?.data) && entity?.data.alumni_information && (
        <>
          <h3 className={styles.subtitle} style={{ marginTop: isMobile ? '32px' : '23px' }}>
            University alumni
          </h3>
          <div
            className={`${styles.alumnisContainer} row flex-row flex-nowrap gap-3 mt-3 pt-1 pr-2`}
          >
            {entity?.data.alumni_information &&
              Object.keys(entity?.data.alumni_information).map(
                (category: string, index: number) => (
                  <div
                    className={`${styles.subject} ${
                      activeCategory === category ? styles.activeSubject : ''
                    }`}
                    key={index}
                    onClick={() => {
                      setActiveCategory(category);
                    }}
                  >
                    {`${category}`}
                  </div>
                )
              )}
          </div>
          <div className={styles.alumniContainer}>
            {entity?.data.alumni_information &&
              entity?.data.alumni_information[activeCategory] &&
              Object.keys(entity.data.alumni_information[activeCategory]).map(
                (subcategory: string, index: number) => (
                  <div className={styles.alumniCategory} key={index}>
                    <div className="d-flex flex-row">
                      <Text
                        size={isMobile ? 12 : 16}
                        weight={isMobile ? 400 : 600}
                      >{`${subcategory}`}</Text>
                      &nbsp;
                      <Text size={isMobile ? 12 : 16} weight={600}>{`(${
                        entityIsSchool(entity?.data)
                          ? entity?.data?.alumni_information?.[activeCategory][subcategory]
                          : ''
                      })`}</Text>
                    </div>
                    <ProgressBar
                      now={
                        entityIsSchool(entity?.data)
                          ? (entity?.data?.alumni_information?.[activeCategory][subcategory] /
                              getMaxFromCategory(activeCategory)) *
                            100
                          : 0
                      }
                      style={!isMobile ? { width: '60%' } : {}}
                    />
                  </div>
                )
              )}
          </div>
        </>
      )}
      {entityIsProgram(entity?.data) && entity?.data.school.alumni_information && (
        <>
          <h3 className={styles.subtitle} style={{ marginTop: isMobile ? '32px' : '23px' }}>
            University alumni
          </h3>
          <div
            className={`${styles.alumnisContainer} row flex-row flex-nowrap gap-3 mt-3 pt-1 pr-2`}
          >
            {entity?.data.school.alumni_information &&
              Object.keys(entity?.data.school.alumni_information).map(
                (category: string, index: number) => (
                  <div
                    className={`${styles.subject} ${
                      activeCategory === category ? styles.activeSubject : ''
                    }`}
                    key={index}
                    onClick={() => {
                      setActiveCategory(category);
                    }}
                  >
                    {`${category}`}
                  </div>
                )
              )}
          </div>
          <div className={styles.alumniContainer}>
            {entity?.data.school.alumni_information &&
              entity?.data.school.alumni_information[activeCategory] &&
              Object.keys(entity.data.school.alumni_information[activeCategory]).map(
                (subcategory: string, index: number) => (
                  <div className={styles.alumniCategory} key={index}>
                    <div className="d-flex flex-row">
                      <Text
                        size={isMobile ? 12 : 16}
                        weight={isMobile ? 400 : 600}
                      >{`${subcategory}`}</Text>
                      &nbsp;
                      <Text size={isMobile ? 12 : 16} weight={600}>{`(${
                        entityIsProgram(entity?.data)
                          ? entity?.data?.school.alumni_information?.[activeCategory][subcategory]
                          : ''
                      })`}</Text>
                    </div>
                    <ProgressBar
                      now={
                        entityIsProgram(entity?.data)
                          ? (entity?.data?.school.alumni_information?.[activeCategory][
                              subcategory
                            ] /
                              getMaxFromCategory(activeCategory)) *
                            100
                          : 0
                      }
                      style={!isMobile ? { width: '60%' } : {}}
                    />
                  </div>
                )
              )}
          </div>
        </>
      )}
      {!!(
        (entityIsSchool(entity?.data) &&
          entity?.data.companies &&
          entity?.data.companies.length > 0) ||
        (entityIsProgram(entity?.data) &&
          entity?.data.school.companies &&
          entity?.data.school.companies.length > 0)
      ) && (
        <>
          <h3 className={styles.subtitle} style={{ marginTop: isMobile ? '32px' : '46px' }}>
            {`Companies that hire from ${
              entityIsSchool(entity?.data) ? entity?.data.name : entity?.data.school.name
            }`}
          </h3>
          <div className={`${styles.companiesContainer} row row-cols-2 row-cols-lg-4 mt-4`}>
            {entityIsSchool(entity?.data)
              ? entity.data.companies.map((company: Company) => (
                  <div className={`col ${styles.company}`} key={company.id}>
                    <div className={styles.blockNumber}>
                      {company.logo ? (
                        <img
                          src={company.logo}
                          alt={`Logo of ${company.name}`}
                          className={styles.accLogoContainer}
                        />
                      ) : (
                        <Text
                          size={isMobile ? 14 : 18}
                          weight={600}
                          inlineStyle={{
                            color: '#19234F',
                            letterSpacing: '0.3px',
                          }}
                        >
                          {company.name}
                        </Text>
                      )}
                    </div>
                  </div>
                ))
              : entity.data.school.companies.map((company: Company) => (
                  <div className={`col ${styles.company}`} key={company.id}>
                    <div className={styles.blockNumber}>
                      {company.logo ? (
                        <img
                          src={company.logo}
                          alt={`Logo of ${company.name}`}
                          className={styles.accLogoContainer}
                        />
                      ) : (
                        <Text
                          size={isMobile ? 14 : 18}
                          weight={600}
                          inlineStyle={{
                            color: '#19234F',
                            letterSpacing: '0.3px',
                          }}
                        >
                          {company.name}
                        </Text>
                      )}
                    </div>
                  </div>
                ))}
          </div>
        </>
      )}
      {!!(
        (entityIsSchool(entity?.data) && entity?.data.career_service_description) ||
        (entityIsProgram(entity?.data) && entity?.data.school.career_service_description)
      ) && (
        <>
          <h3 className={styles.subtitle}>Career services</h3>
          <Text
            size={isMobile ? 14 : 16}
            weight={400}
            inlineStyle={{
              color: '#19234F',
              marginTop: isMobile ? '6px' : '24px',
              marginBottom: isMobile ? '10px' : '80px',
            }}
          >
            {entityIsSchool(entity?.data)
              ? entity?.data.career_service_description
              : entity?.data.school.career_service_description}
          </Text>
        </>
      )}
    </div>
  );
};
